import type {
  UpdateTaxCodeMutationOptions,
  UpdateTaxCodeVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysTaxCode } from '../queryKeys';

import { updateTaxCode } from './updateTaxCode';

/**
 * Updates one tax code and refreshes detail, lists, and filter options.
 *
 * @param options - Additional mutation options.
 * @returns The tax-code update mutation.
 * @see https://app.dev.vestigas.com/redoc#tag/Tax-Codes
 */
export const useMutationUpdateTaxCode = (
  options?: UpdateTaxCodeMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ payload, taxCodeId }: UpdateTaxCodeVariables) =>
      updateTaxCode(taxCodeId, payload),
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysTaxCode.getAll(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysTaxCode.get(variables.taxCodeId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysTaxCode.filterOptions(),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
};
